import { useState } from 'react';
import { IconChevronDown, IconChevronUp, IconFileText, IconTool } from '@tabler/icons-react';
import { COLORS } from '../../utils/uiStyles';
import { WORKSPACE_MAX_UPLOAD_MB, workspaceRestrictionReason, workspaceRoleCan } from './hooks/useWorkspace';

const fileNameOf = (file) => file?.filename || file?.name || String(file);
const WS_PANEL_COLLAPSED_KEY = 'ws_panel_collapsed';

const fileSizeOf = (file) => {
  const size = Number(file?.size || 0);
  return size > 0 ? ` (${size.toLocaleString()}B)` : '';
};

const readInitialCollapsed = () => {
  try {
    return localStorage.getItem(WS_PANEL_COLLAPSED_KEY) !== '0';
  } catch {
    return true;
  }
};

const persistCollapsed = (nextCollapsed) => {
  try {
    localStorage.setItem(WS_PANEL_COLLAPSED_KEY, nextCollapsed ? '1' : '0');
  } catch {
    // Ignore unavailable storage, such as private browsing restrictions.
  }
};

export default function WorkspacePanel({
  mobile = false,
  selectedProjectId,
  projectName = '선택한 프로젝트',
  accessRole = 'owner',
  cowork = false,
  toggleCowork,
  workspaceFiles = [],
  workspaceBusy = false,
  wsFileRef,
  canRead,
  canDownload,
  canUpload,
  canDelete,
  uploadWorkspaceFile,
  downloadWorkspaceFile,
  deleteWorkspaceFile,
}) {
  const [collapsed, setCollapsed] = useState(readInitialCollapsed);

  if (!selectedProjectId) return null;

  const allowRead = canRead ?? workspaceRoleCan(accessRole, 'read');
  const allowDownload = canDownload ?? workspaceRoleCan(accessRole, 'download');
  const allowUpload = canUpload ?? workspaceRoleCan(accessRole, 'upload');
  const allowDelete = canDelete ?? workspaceRoleCan(accessRole, 'delete');
  const allowWrite = workspaceRoleCan(accessRole, 'write');
  const workspaceBodyId = 'workspace-panel-body';
  const fileCount = workspaceFiles.length;
  const toggleCollapsed = () => {
    setCollapsed((wasCollapsed) => {
      const nextCollapsed = !wasCollapsed;
      persistCollapsed(nextCollapsed);
      return nextCollapsed;
    });
  };
  const expandPanel = () => {
    persistCollapsed(false);
    setCollapsed(false);
  };
  const handleCoworkToggle = (event) => {
    event.stopPropagation();
    if (!allowRead) return;
    if (!cowork) expandPanel();
    if (typeof toggleCowork === 'function') toggleCowork();
  };
  const uploadTitle = allowUpload
    ? `작업공간 파일 올리기 (최대 ${WORKSPACE_MAX_UPLOAD_MB}MB)`
    : workspaceRestrictionReason(accessRole, 'upload');
  const writeRestriction = allowWrite ? '' : workspaceRestrictionReason(accessRole, 'write');
  const deleteRestriction = allowDelete ? '' : workspaceRestrictionReason(accessRole, 'delete');
  const downloadRestriction = allowDownload ? '' : workspaceRestrictionReason(accessRole, 'download');

  const panelStyle = {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(15,23,42,.05)',
    padding: collapsed ? (mobile ? '6px 10px' : '6px 12px') : (mobile ? '12px' : '14px 16px'),
  };
  const toggleStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: '32px',
    border: `1px solid ${cowork ? COLORS.primary : '#d1d5db'}`,
    borderRadius: '16px',
    background: cowork ? COLORS.primary : 'white',
    color: cowork ? 'white' : '#374151',
    cursor: allowRead ? 'pointer' : 'not-allowed',
    opacity: allowRead ? 1 : 0.55,
    padding: '0 11px',
    fontSize: '12px',
    fontWeight: 750,
    flexShrink: 0,
  };

  return (
    <section aria-label="작업공간 파일" style={panelStyle}>
      <div
        onClick={toggleCollapsed}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: collapsed ? 0 : '12px', cursor: 'pointer' }}
      >
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: '15px', color: '#111827', display: 'flex', alignItems: 'center', gap: '7px' }}>
            <IconTool size={17} stroke={1.8} aria-hidden="true" />작업공간 파일
            {collapsed && (
              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 700 }}>
                · {fileCount}
              </span>
            )}
          </h2>
          {!collapsed && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {projectName} · {accessRole} · {allowWrite ? 'AI 읽기/쓰기' : 'AI 읽기 전용'}
            </div>
          )}
        </div>
        <div onClick={(event) => event.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleCoworkToggle}
            aria-pressed={cowork}
            aria-label="작업공간 도구 전환"
            disabled={!allowRead}
            title={allowRead ? (allowWrite ? '작업공간 도구' : writeRestriction) : workspaceRestrictionReason(accessRole, 'read')}
            style={toggleStyle}
          >
            <IconTool size={15} stroke={1.8} aria-hidden="true" />
            작업공간 {cowork ? 'ON' : 'OFF'}
          </button>
          <label
            title={uploadTitle}
            aria-disabled={!allowUpload}
            style={{ fontSize: '13px', color: allowUpload ? COLORS.primaryDark : '#9ca3af', cursor: allowUpload ? 'pointer' : 'not-allowed', fontWeight: 750, display: 'inline-flex', alignItems: 'center', minHeight: '32px' }}
          >
            + 파일 올리기
            <input
              ref={wsFileRef}
              type="file"
              multiple
              onChange={uploadWorkspaceFile}
              aria-label="작업공간 파일 올리기"
              disabled={!allowUpload}
              style={{ display: 'none' }}
            />
          </label>
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls={workspaceBodyId}
            aria-label={`작업공간 파일 ${collapsed ? '펼치기' : '접기'}`}
            title={`작업공간 파일 ${collapsed ? '펼치기' : '접기'}`}
            style={{ width: '32px', height: '32px', border: '1px solid #d1d5db', borderRadius: '16px', background: 'white', color: '#374151', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}
          >
            {collapsed ? <IconChevronDown size={17} stroke={1.9} aria-hidden="true" /> : <IconChevronUp size={17} stroke={1.9} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {!collapsed && (
        <div id={workspaceBodyId} style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
          <span style={{ fontSize: '11px', color: '#9ca3af', alignSelf: 'center' }}>
            최대 {WORKSPACE_MAX_UPLOAD_MB}MB
          </span>
          {!allowWrite && (
            <span role="note" style={{ fontSize: '12px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '4px 7px' }}>
              {writeRestriction}
            </span>
          )}
          {workspaceBusy && (
            <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
              <span aria-hidden="true" style={{ width: '10px', height: '10px', border: `2px solid ${COLORS.primaryBorder}`, borderTopColor: COLORS.primary, borderRadius: '50%', display: 'inline-block', animation: 'docspin 0.7s linear infinite' }} />
              작업공간 동기화 중
            </span>
          )}
          {!workspaceBusy && workspaceFiles.length === 0 && (
            <span style={{ fontSize: '13px', color: '#6b7280' }}>아직 파일이 없습니다. AI에게 파일 작성을 요청하거나 직접 올려보세요.</span>
          )}
          {workspaceFiles.map((file) => {
            const filename = fileNameOf(file);
            return (
              <div key={filename} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 10px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '999px', fontSize: '12px', color: '#374151', maxWidth: '100%', minWidth: 0 }}>
                <IconFileText size={14} stroke={1.8} color={COLORS.primary} aria-hidden="true" style={{ flexShrink: 0 }} />
                <button
                  type="button"
                  onClick={() => downloadWorkspaceFile(filename)}
                  disabled={!allowDownload}
                  title={allowDownload ? '클릭하여 다운로드' : downloadRestriction}
                  style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', border: 'none', background: 'transparent', color: allowDownload ? '#374151' : '#9ca3af', cursor: allowDownload ? 'pointer' : 'not-allowed', padding: 0, font: 'inherit', textAlign: 'left' }}
                >
                  {filename}
                  {file.readable === false && (
                    <span title="AI가 내용을 읽을 수 없는 형식입니다(다운로드만 가능)" style={{ color: '#9ca3af' }}>· 읽기 불가</span>
                  )}
                  <span style={{ color: '#9ca3af' }}>{fileSizeOf(file)}</span>
                </button>
                <button
                  type="button"
                  onClick={() => deleteWorkspaceFile(filename)}
                  aria-label={`${filename} 삭제`}
                  disabled={!allowDelete}
                  title={allowDelete ? `${filename} 삭제` : deleteRestriction}
                  style={{ background: 'none', border: 'none', cursor: allowDelete ? 'pointer' : 'not-allowed', color: allowDelete ? '#94a3b8' : '#cbd5e1', padding: 0, lineHeight: 1, fontSize: '15px' }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
